<template>
  <div class="min-h-screen bg-background">
    <!-- Header Banner -->
    <div class="bg-gradient-hero py-8 md:py-12">
      <div class="container mx-auto px-4">
        <h1 class="text-3xl md:text-4xl font-bold text-primary-foreground mb-2">
          {{ pageTitle }}
        </h1>
        <p class="text-primary-foreground/80">
          {{ sortedProducts.length }} produit{{ sortedProducts.length > 1 ? 's' : '' }} trouvé{{ sortedProducts.length > 1 ? 's' : '' }}
        </p>
      </div>
    </div>

    <div class="container px-2 py-8">
      <div class="flex flex-col lg:flex-row gap-8">
        <!-- Filters Sidebar - Desktop -->
        <div class="hidden lg:block lg:w-1/4">
          <div class="sticky top-20 space-y-6">
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold text-foreground flex items-center gap-2">
                <SlidersHorizontal class="h-5 w-5" />
                Filtres
              </h3>
              <button @click="clearFilters" class="text-sm text-secondary hover:text-secondary-hover transition-normal">
                Effacer tout
              </button>
            </div>

            <!-- Search -->
            <!-- <div class="bg-card rounded-xl border border-border shadow-md">
              <div class="p-4">
                <h4 class="font-medium text-foreground mb-3">Recherche</h4>
                <div class="relative">
                  <Search class="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <input
                    placeholder="Rechercher..."
                    :value="searchQuery"
                    class="pl-10 w-full px-3 py-2 border border-input bg-background rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-normal"
                  />
                </div>
              </div>
            </div> -->

            <!-- Price Range -->
            <div class="bg-card rounded-xl border border-border shadow-md">
              <div class="p-4">
                <h4 class="font-medium text-foreground mb-3">Prix (FCFA)</h4>
                <div class="mb-4">
                  <input
                    type="range"
                    v-model.number="priceRange[0]"
                    :min="0"
                    :max="maxPrice"
                    step="5000"
                    class="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                    @input="updatePriceRange"
                  />
                  <input
                    type="range"
                    v-model.number="priceRange[1]"
                    :min="0"
                    :max="maxPrice"
                    step="5000"
                    class="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer mt-2"
                    @input="updatePriceRange"
                  />
                </div>
                <div class="flex justify-between text-sm text-muted-foreground">
                  <span>{{ formatPrice(priceRange[0]) }}</span>
                  <span>{{ formatPrice(priceRange[1]) }}</span>
                </div>
              </div>
            </div>

            <!-- Categories -->
            <div v-if="availableCategories.length > 0" class="bg-card rounded-xl border border-border shadow-md">
              <div class="p-4">
                <h4 class="font-medium text-foreground mb-3">Catégories</h4>
                <div class="space-y-2 max-h-60 overflow-y-auto">
                  <label
                    v-for="category in availableCategories"
                    :key="category.id"
                    class="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedCategories.includes(category.id)"
                      @change="handleCategoryToggle(category.id)"
                      class="h-4 w-4 text-primary rounded focus:ring-ring"
                    />
                    <span class="text-sm text-foreground">{{ category.name }}</span>
                    <span class="text-xs text-muted-foreground ml-auto">
                      ({{ getProductCountByCategory(category.id) }})
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Brands -->
            <div v-if="availableBrands.length > 0" class="bg-card rounded-xl border border-border shadow-md">
              <div class="p-4">
                <h4 class="font-medium text-foreground mb-3">Marques</h4>
                <div class="space-y-2 max-h-60 overflow-y-auto">
                  <label
                    v-for="brand in availableBrands"
                    :key="brand.id"
                    class="flex items-center space-x-2 cursor-pointer hover:bg-muted/50 p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedBrands.includes(brand.id)"
                      @change="handleBrandToggle(brand.id)"
                      class="h-4 w-4 text-primary rounded focus:ring-ring"
                    />
                    <span class="text-sm text-foreground">{{ brand.name }}</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Status -->
            <div class="bg-card rounded-xl border border-border shadow-md">
              <div class="p-4">
                <h4 class="font-medium text-foreground mb-3">Disponibilité</h4>
                <div class="space-y-2">
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="inStockOnly"
                      class="h-4 w-4 text-primary rounded focus:ring-ring"
                    />
                    <span class="text-sm text-foreground">En stock seulement</span>
                  </label>
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="promoOnly"
                      class="h-4 w-4 text-primary rounded focus:ring-ring"
                    />
                    <span class="text-sm text-foreground">Produits en promotion</span>
                  </label>
                </div>
              </div>
            </div>

            <!-- Rating -->
            <div class="bg-card rounded-xl border border-border shadow-md">
              <div class="p-4">
                <h4 class="font-medium text-foreground mb-3">Note minimum</h4>
                <div class="space-y-2">
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      v-model="minRating"
                      value="0"
                      class="h-4 w-4 text-primary"
                    />
                    <span class="text-sm text-foreground">Toutes</span>
                  </label>
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      v-model="minRating"
                      value="4"
                      class="h-4 w-4 text-primary"
                    />
                    <div class="flex">
                      <Star v-for="i in 5" :key="i" class="h-3 w-3 text-amber-500 fill-amber-500" />
                    </div>
                    <span class="text-sm text-foreground ml-1">4+ étoiles</span>
                  </label>
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      v-model="minRating"
                      value="3"
                      class="h-4 w-4 text-primary"
                    />
                    <div class="flex">
                      <Star v-for="i in 3" :key="i" class="h-3 w-3 text-amber-500 fill-amber-500" />
                    </div>
                    <span class="text-sm text-foreground ml-1">3+ étoiles</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Filter Button -->
        <div class="lg:hidden flex items-center justify-between mb-4">
          <button 
            @click="showFilters = true"
            class="flex items-center gap-2 px-4 py-2 border border-input bg-background text-foreground rounded-lg hover:bg-muted transition-normal"
          >
            <Filter class="h-4 w-4" />
            Filtres
          </button>
          
          <select 
            v-model="sortBy"
            class="px-3 py-2 border border-input bg-background text-foreground rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-normal"
          >
            <option value="default">Par défaut</option>
            <option value="price-asc">Prix croissant</option>
            <option value="price-desc">Prix décroissant</option>
            <option value="name-asc">Nom A-Z</option>
            <option value="name-desc">Nom Z-A</option>
            <option value="newest">Plus récent</option>
            <option value="rating">Mieux notés</option>
          </select>
        </div>

        <!-- Mobile Filters Modal -->
        <div v-if="showFilters" class="fixed inset-0 z-50 bg-background lg:hidden">
          <div class="flex flex-col h-full">
            <div class="flex items-center justify-between p-4 border-b border-border">
              <h3 class="text-lg font-semibold text-foreground">Filtres</h3>
              <button @click="showFilters = false" class="p-2 hover:bg-muted rounded-lg transition-normal">
                <X class="h-5 w-5 text-muted-foreground" />
              </button>
            </div>
            
            <div class="flex-1 overflow-y-auto p-4 space-y-6">
              <!-- Price Range -->
              <div>
                <h4 class="font-medium text-foreground mb-3">Prix (FCFA)</h4>
                <div class="mb-4">
                  <input
                    type="range"
                    v-model.number="priceRange[0]"
                    :min="0"
                    :max="maxPrice"
                    step="5000"
                    class="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer"
                    @input="updatePriceRange"
                  />
                  <input
                    type="range"
                    v-model.number="priceRange[1]"
                    :min="0"
                    :max="maxPrice"
                    step="5000"
                    class="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer mt-2"
                    @input="updatePriceRange"
                  />
                </div>
                <div class="flex justify-between text-sm text-muted-foreground">
                  <span>{{ formatPrice(priceRange[0]) }}</span>
                  <span>{{ formatPrice(priceRange[1]) }}</span>
                </div>
              </div>

              <!-- Categories -->
              <div v-if="availableCategories.length > 0">
                <h4 class="font-medium text-foreground mb-3">Catégories</h4>
                <div class="space-y-2">
                  <label
                    v-for="category in availableCategories"
                    :key="category.id"
                    class="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedCategories.includes(category.id)"
                      @change="handleCategoryToggle(category.id)"
                      class="h-4 w-4 text-primary rounded focus:ring-ring"
                    />
                    <span class="text-sm text-foreground">{{ category.name }}</span>
                  </label>
                </div>
              </div>

              <!-- Brands -->
              <div v-if="availableBrands.length > 0">
                <h4 class="font-medium text-foreground mb-3">Marques</h4>
                <div class="space-y-2">
                  <label
                    v-for="brand in availableBrands"
                    :key="brand.id"
                    class="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      :checked="selectedBrands.includes(brand.id)"
                      @change="handleBrandToggle(brand.id)"
                      class="h-4 w-4 text-primary rounded focus:ring-ring"
                    />
                    <span class="text-sm text-foreground">{{ brand.name }}</span>
                  </label>
                </div>
              </div>

              <!-- Status -->
              <div>
                <h4 class="font-medium text-foreground mb-3">Disponibilité</h4>
                <div class="space-y-2">
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="inStockOnly"
                      class="h-4 w-4 text-primary rounded focus:ring-ring"
                    />
                    <span class="text-sm text-foreground">En stock seulement</span>
                  </label>
                  <label class="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      v-model="promoOnly"
                      class="h-4 w-4 text-primary rounded focus:ring-ring"
                    />
                    <span class="text-sm text-foreground">Produits en promotion</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div class="p-4 border-t border-border flex gap-4">
              <button @click="clearFilters" class="flex-1 px-4 py-2 border border-input bg-background text-foreground rounded-lg hover:bg-muted transition-normal">
                Effacer
              </button>
              <button @click="showFilters = false" class="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-hover transition-normal">
                Voir {{ filteredProducts.length }} résultats
              </button>
            </div>
          </div>
        </div>

        <!-- Products Section -->
        <div class="lg:w-3/4">
          <!-- Sort and View - Desktop -->
          <div class="hidden lg:flex items-center justify-between mb-6">
            <div class="flex items-center space-x-4">
              <select 
                v-model="sortBy"
                class="px-3 py-2 border border-input bg-background text-foreground rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-normal"
              >
                <option value="default">Par défaut</option>
                <option value="price-asc">Prix croissant</option>
                <option value="price-desc">Prix décroissant</option>
                <option value="name-asc">Nom A-Z</option>
                <option value="name-desc">Nom Z-A</option>
                <option value="newest">Plus récent</option>
                <option value="rating">Mieux notés</option>
              </select>
              <div class="text-sm text-muted-foreground">
                <span v-if="isFiltered">Filtres actifs: {{ activeFilterCount }}</span>
              </div>
            </div>
            
            <div class="flex items-center gap-4">
              <div class="text-sm text-muted-foreground">
                Page {{ currentPage }} sur {{ totalPages }}
              </div>
              <div class="flex border border-input rounded-lg">
                <button
                  @click="viewMode = 'grid'"
                  :class="`px-3 py-1.5 rounded-l-lg transition-normal ${
                    viewMode === 'grid' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background text-foreground hover:bg-muted'
                  }`"
                  title="Vue grille"
                >
                  <Grid class="h-4 w-4" />
                </button>
                <button
                  @click="viewMode = 'list'"
                  :class="`px-3 py-1.5 rounded-r-lg transition-normal ${
                    viewMode === 'list' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-background text-foreground hover:bg-muted'
                  }`"
                  title="Vue liste"
                >
                  <List class="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <!-- Applied Filters -->
          <div v-if="isFiltered" class="flex flex-wrap gap-2 mb-6">
            <div 
              v-for="categoryId in selectedCategories"
              :key="'cat-' + categoryId"
              class="flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
            >
              {{ getCategoryName(categoryId) }}
              <button @click="removeCategory(categoryId)" class="hover:text-primary/70">
                <X class="h-3 w-3" />
              </button>
            </div>
            <div 
              v-for="brandId in selectedBrands"
              :key="'brand-' + brandId"
              class="flex items-center gap-1 px-3 py-1 bg-secondary/10 text-secondary rounded-full text-sm"
            >
              {{ getBrandName(brandId) }}
              <button @click="removeBrand(brandId)" class="hover:text-secondary/70">
                <X class="h-3 w-3" />
              </button>
            </div>
            <div 
              v-if="priceRange[0] > 0 || priceRange[1] < maxPrice"
              class="flex items-center gap-1 px-3 py-1 bg-accent/10 text-accent rounded-full text-sm"
            >
              Prix: {{ formatPrice(priceRange[0]) }} - {{ formatPrice(priceRange[1]) }}
              <button @click="resetPriceRange" class="hover:text-accent/70">
                <X class="h-3 w-3" />
              </button>
            </div>
            <div 
              v-if="inStockOnly"
              class="flex items-center gap-1 px-3 py-1 bg-green-500/10 text-green-600 rounded-full text-sm"
            >
              En stock
              <button @click="inStockOnly = false" class="hover:text-green-600/70">
                <X class="h-3 w-3" />
              </button>
            </div>
            <div 
              v-if="promoOnly"
              class="flex items-center gap-1 px-3 py-1 bg-red-500/10 text-red-600 rounded-full text-sm"
            >
              Promotion
              <button @click="promoOnly = false" class="hover:text-red-600/70">
                <X class="h-3 w-3" />
              </button>
            </div>
            <div 
              v-if="minRating > 0"
              class="flex items-center gap-1 px-3 py-1 bg-amber-500/10 text-amber-600 rounded-full text-sm"
            >
              Note: {{ minRating }}+
              <button @click="minRating = 0" class="hover:text-amber-600/70">
                <X class="h-3 w-3" />
              </button>
            </div>
          </div>

          <!-- Products Grid -->
          <div v-if="sortedProducts.length > 0">
            <div :class="viewMode === 'grid' 
              ? 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4'
              : 'space-y-4'">
              <div
                v-for="product in paginatedProducts"
                :key="product.id"
                class="bg-card rounded-md border border-border shadow-md hover:shadow-xl transition-normal group overflow-hidden"
              >
                <template v-if="viewMode === 'list'">
                  <div class="flex flex-col sm:flex-row">
                    <div class="sm:w-48 aspect-square sm:aspect-auto relative">
                      <router-link :to="`/product/${product.slug}`">
                        <img 
                        loading="lazy"
                          :src="getProductImage(product)" 
                          :alt="product.name"
                          class="w-full h-full object-cover"
                          @error="handleImageError"
                        />
                      </router-link>
                      <span 
                        v-if="hasDiscount(product)" 
                        class="absolute top-2 left-2 bg-destructive text-destructive-foreground px-2 py-1 rounded text-xs font-semibold"
                      >
                        -{{ calculateDiscount(product) }}%
                      </span>
                    </div>
                    <div class="flex-1 p-4">
                      <!-- Category from product.category object -->
                      <span class="text-xs text-muted-foreground">
                        {{ product.category?.name || product.model?.category?.name || 'Non catégorisé' }}
                      </span>
                      <router-link :to="`/product/${product.slug}`">
                        <h3 class="font-semibold text-foreground hover:text-secondary transition-normal mt-1">
                          {{ product.name }}
                        </h3>
                      </router-link>
                      <p class="text-sm text-muted-foreground mt-1 line-clamp-2">
                        {{ product.description?.substring(0, 100) }}{{ product.description?.length > 100 ? '...' : '' }}
                      </p>
                      <!-- Rating from product.rating_avg -->
                      <div class="flex items-center gap-1 mt-2" v-if="product.rating_avg > 0">
                        <div class="flex">
                          <Star
                            v-for="i in 5"
                            :key="i"
                            :class="[
                              'h-3 w-3',
                              i <= Math.floor(product.rating_avg) 
                                ? 'text-secondary fill-secondary' 
                                : 'text-muted-foreground/30'
                            ]"
                          />
                        </div>
                        <span class="text-xs text-muted-foreground">({{ product.reviews_count || 0 }})</span>
                      </div>
                      <div class="flex items-center gap-2 mt-2">
                        <span class="text-lg font-bold text-foreground">
                          {{ formatPrice(getProductPrice(product)) }}
                        </span>
                        <span 
                          v-if="hasDiscount(product)" 
                          class="text-sm text-muted-foreground line-through"
                        >
                          {{ formatPrice(product.price) }}
                        </span>
                      </div>
                      <div class="flex gap-2 mt-4">
                        <button 
                          @click="addToCart(product)" 
                          :disabled="!product.in_stock || product.stock_quantity <= 0"
                          :class="[
                            'flex-1 flex items-center justify-center gap-2 px-3 py-1.5 rounded-lg transition-normal',
                            product.in_stock && product.stock_quantity > 0
                              ? 'bg-primary text-primary-foreground hover:bg-primary-hover'
                              : 'bg-gray-400 cursor-not-allowed text-gray-700'
                          ]"
                        >
                          <ShoppingCart class="h-4 w-4" />
                          {{ product.stock_quantity > 0 ? 'Ajouter au Panier' : 'Rupture' }}
                        </button>
                        <button 
                          @click="toggleWishlist(product)"
                          class="px-3 py-1.5 border border-input bg-background text-foreground rounded-lg hover:bg-muted transition-normal"
                          :title="isInWishlist(product.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'"
                        >
                          <Heart 
                            :class="[
                              'h-4 w-4',
                              isInWishlist(product.id) 
                                ? 'text-destructive fill-destructive' 
                                : 'text-muted-foreground'
                            ]"
                          />
                        </button>
                      </div>
                      <!-- Stock info -->
                      <div v-if="product.stock_quantity <= 10 && product.stock_quantity > 0" class="mt-2">
                        <p class="text-xs text-amber-600">
                          Plus que {{ product.stock_quantity }} disponible{{ product.stock_quantity > 1 ? 's' : '' }}
                        </p>
                      </div>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div class="relative">
                    <router-link :to="`/product/${product.category?.slug}/${product.slug}`">
                      <div class="aspect-square overflow-hidden bg-muted">
                        <img 
                          loading="lazy"
                          :src="getProductImage(product)" 
                          :alt="product.name"
                          class="w-full h-full object-cover group-hover:scale-105 transition-normal duration-300"
                          @error="handleImageError"
                        />
                      </div>
                    </router-link>
                    
                    <div class="absolute top-3 left-3 flex flex-col gap-2">
                      <span 
                        v-if="hasDiscount(product)" 
                        class="bg-destructive text-destructive-foreground px-2 py-1 rounded-sm text-xs font-semibold"
                      >
                        -{{ calculateDiscount(product) }}%
                      </span>
                      <span 
                        v-if="getProductBadge(product)" 
                        :class="getBadgeClass(getProductBadge(product))"
                      >
                        {{ getProductBadge(product) }}
                      </span>
                      <span 
                        v-if="!product.in_stock" 
                        class="bg-gray-500 text-white px-2 py-1 rounded-sm text-xs font-semibold"
                      >
                        Indisponible
                      </span>
                    </div>

                    <button
                      @click="toggleWishlist(product)"
                      class="absolute top-3 right-3 w-8 h-8 bg-background/80 hover:bg-background rounded-full flex items-center justify-center transition-normal"
                      :title="isInWishlist(product.id) ? 'Retirer des favoris' : 'Ajouter aux favoris'"
                    >
                      <Heart 
                        :class="[
                          'h-4 w-4',
                          isInWishlist(product.id) 
                            ? 'text-destructive fill-destructive' 
                            : 'text-muted-foreground'
                        ]"
                      />
                    </button>

                     <div
                class="absolute bottom-3 left-3 right-3 opacity-50 z-30 group-hover:opacity-100 transition-opacity duration-300">
                <button @click="addToCart(product)" :disabled="!product.in_stock || product.stock_quantity <= 0"
                  :class="[
                    'w-full py-2 rounded-lg transition-normal flex items-center justify-center',
                    product.in_stock && product.stock_quantity > 0
                      ? 'bg-primary hover:bg-primary-hover text-primary-foreground'
                      : 'bg-gray-400 cursor-not-allowed text-gray-700'
                  ]">
                  <ShoppingCart class="h-4 w-4 mr-2" />
                  {{ product.stock_quantity > 0 ? 'Ajouter ' : 'Rupture' }}
                </button>
              </div>
                  </div>

                  <div class="p-4">
                    <!-- Category from nested object -->
                    <span class="text-xs text-muted-foreground">
                      {{ product.category?.name || product.model?.category?.name || 'Non catégorisé' }}
                    </span>
                    <router-link :to="`/product/${product.category?.slug}/${product.slug}`">
                      <h3 class="font-semibold text-foreground hover:text-secondary transition-normal mt-1 line-clamp-2">
                        {{ product.name }}
                      </h3>
                    </router-link>

                    <!-- Rating from product.rating_avg -->
                    <div class="flex items-center gap-1 mt-2" v-if="product.rating_avg > 0">
                      <div class="flex">
                        <Star
                          v-for="i in 5"
                          :key="i"
                          :class="[
                            'h-3 w-3',
                            i <= Math.floor(product.rating_avg) 
                              ? 'text-secondary fill-secondary' 
                              : 'text-muted-foreground/30'
                          ]"
                        />
                      </div>
                      <span class="text-xs text-muted-foreground">({{ product.reviews_count || 0 }})</span>
                    </div>

                    <div class="flex items-center gap-2 mt-2">
                      <span class="text-lg font-bold text-foreground">
                        {{ formatPrice(getProductPrice(product)) }}
                      </span>
                      <span 
                        v-if="hasDiscount(product)" 
                        class="text-sm text-muted-foreground line-through"
                      >
                        {{ formatPrice(product.price) }}
                      </span>
                    </div>
                    
                    <!-- Stock info -->
                    <div v-if="product.stock_quantity <= 10 && product.stock_quantity > 0" class="mt-1">
                      <p class="text-xs text-amber-600">
                        Plus que {{ product.stock_quantity }} disponible{{ product.stock_quantity > 1 ? 's' : '' }}
                      </p>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Pagination -->
            <div v-if="totalPages > 1" class="flex justify-center items-center gap-2 mt-8">
              <button
                @click="prevPage"
                :disabled="currentPage === 1"
                class="px-3 py-2 border border-input bg-background text-foreground rounded-lg hover:bg-muted transition-normal disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              
              <div class="flex gap-1">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="goToPage(page)"
                  :class="[
                    'w-10 h-10 flex items-center justify-center rounded-lg transition-normal',
                    currentPage === page 
                      ? 'bg-primary text-primary-foreground' 
                      : 'border border-input bg-background text-foreground hover:bg-muted'
                  ]"
                >
                  {{ page }}
                </button>
              </div>
              
              <button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                class="px-3 py-2 border border-input bg-background text-foreground rounded-lg hover:bg-muted transition-normal disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>

          <!-- Loading State -->
          <div v-else-if="loading" class="text-center py-12">
            <div class="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            <p class="mt-4 text-muted-foreground">Chargement des produits...</p>
          </div>

          <!-- No Results -->
          <div v-else class="text-center py-12">
            <Package class="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <p class="text-lg text-muted-foreground mb-4">
              Aucun produit trouvé avec ces critères
            </p>
            <button @click="clearFilters" class="px-4 py-2 border border-input bg-background text-foreground rounded-lg hover:bg-muted transition-normal">
              Effacer les filtres
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
 
import { 
  SlidersHorizontal,
  Search,
  Filter,
  X,
  Grid,
  List,
  ShoppingCart,
  Heart,
  Star,
  Package
} from 'lucide-vue-next';

export default {
  
  name: 'CategoryPage',
  components: {
    SlidersHorizontal,
    Search,
    Filter,
    X,
    Grid,
    List,
    ShoppingCart,
    Heart,
    Star,
    Package
  },
  data() {
    return {
     
      priceRange: [0, 500000],
      selectedCategories: [],
      selectedBrands: [],
      sortBy: 'default',
      viewMode: 'grid',
      showFilters: false,
      inStockOnly: false,
      promoOnly: false,
      minRating: 0,
      currentPage: 1,
      itemsPerPage: 12,
      
      // Loading state
      loading: false,
      
      // Default image
      defaultProductImage: require('@/assets/default-product.png'),
    };
  },
  computed: {
    // Paramètre de route
    category() {
      return this.$route.params.category || 'all';
    },
    searchQuery() {
      return this.$route.query.q || '';
    },
    
    // Titre de la page basé sur la catégorie
    pageTitle() {
      const titles = {
        all: 'Tous les Produits',
        trending: 'Produits Tendances',
        new: 'Nouveautés',
        promos: 'Produits en Promotion',
        bestsellers: 'Meilleures Ventes'
      };
      
      if (this.category in titles) {
        return titles[this.category];
      }
      
      // Vérifier si c'est une catégorie par ID ou slug
      const category = this.availableCategories.find(c => 
        c.slug === this.category 
      );
      return category ? category.name : 'Produits';
    },
    
    // Récupération des données depuis le store Vuex
    products() {
     
      return this.$store.state.allProducts || [];
    },
    
    availableCategories() {
      return this.$store.state.categories || [];
    },
    
    availableBrands() {
      return this.$store.state.brands || [];
    },
    
    wishlist() {
      return this.$store.state.wishlist || [];
    },
    
    // Prix maximum pour le slider
    maxPrice() {
      if (this.products.length === 0) return 500000;
      const maxProductPrice = Math.max(...this.products.map(p => {
        const price = parseFloat(p.price) || 0;
        const promoPrice = parseFloat(p.promo_price) || price;
        return Math.max(price, promoPrice);
      }));
      return Math.max(maxProductPrice + 10000, 500000);
    },
    
    // Filtrage des produits
    filteredProducts() {
      if (this.products.length === 0) return [];
      
      return this.products.filter(product => {
        // Filtre par recherche
        if (this.searchQuery) {
          const searchLower = this.searchQuery.toLowerCase();
          const matchesSearch = 
            product.name.toLowerCase().includes(searchLower) ||
            (product.description && product.description.toLowerCase().includes(searchLower)) ||
            (product.category?.name && product.category.name.toLowerCase().includes(searchLower)) ||
            (product.model?.category?.name && product.model.category.name.toLowerCase().includes(searchLower)) ||
            (product.brand?.name && product.brand.name.toLowerCase().includes(searchLower)) ||
            (product.model?.brand?.name && product.model.brand.name.toLowerCase().includes(searchLower));
          if (!matchesSearch) return false;
        }
        
        // Filtre par prix
        const productPrice = this.getProductPrice(product);
        if (productPrice < this.priceRange[0] || productPrice > this.priceRange[1]) {
          return false;
        }
        
        // Filtre par catégorie
        if (this.selectedCategories.length > 0) {
          const categoryId = product.category?.id || product.model?.category?.id;
          if (!categoryId || !this.selectedCategories.includes(categoryId)) {
            return false;
          }
        }
        
        // Filtre par marque
        if (this.selectedBrands.length > 0) {
          const brandId = product.brand?.id || product.model?.brand?.id;
          if (!brandId || !this.selectedBrands.includes(brandId)) {
            return false;
          }
        }
        
        // Filtre par disponibilité
        if (this.inStockOnly && (!product.in_stock || product.stock_quantity <= 0)) {
          return false;
        }
        
        // Filtre par promotion
        if (this.promoOnly && !product.promo_price) {
          return false;
        }
        
        // Filtre par note
        if (this.minRating > 0 && (!product.rating_avg || product.rating_avg < this.minRating)) {
          return false;
        }
        
        // Filtre par catégorie spécifique (paramètre de route)
        if (this.category && this.category !== 'all') {
          if (this.category === 'trending') {
            // Produits avec beaucoup de vues
            if (product.badge!=='tendance' ) return false;
          } else if (this.category === 'new') {
            // Produits créés récemment (moins de 30 jours)
            if (product.created_at) {
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              const createdDate = new Date(product.created_at);
              if (createdDate < thirtyDaysAgo) return false;
            } else {
              return false;
            }
          } else if (this.category === 'promos') {
            // Produits en promotion
            if (product.badge!=='promotion') return false;
          } else if (this.category === 'bestsellers') {
         
            if (product.badge!=='best_seller') return false;
          } else {
            // Catégorie spécifique par slug
            const routeCategory = this.availableCategories.find(c => 
              c.slug === this.category
            );
            if (routeCategory) {
              const productCategoryId = product.category?.id || product.model?.category?.id;
              if (productCategoryId !== routeCategory.id) {
                return false;
              }
            }
          }
        }
        
        return true;
      });
    },
    
    // Tri des produits
    sortedProducts() {
      const products = [...this.filteredProducts];
      
      switch (this.sortBy) {
        case 'price-asc':
          return products.sort((a, b) => this.getProductPrice(a) - this.getProductPrice(b));
        case 'price-desc':
          return products.sort((a, b) => this.getProductPrice(b) - this.getProductPrice(a));
        case 'name-asc':
          return products.sort((a, b) => a.name.localeCompare(b.name));
        case 'name-desc':
          return products.sort((a, b) => b.name.localeCompare(a.name));
        case 'newest':
          return products.sort((a, b) => {
            const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
            const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
            return dateB - dateA;
          });
        case 'rating':
          return products.sort((a, b) => (b.rating_avg || 0) - (a.rating_avg || 0));
        default:
          return products;
      }
    },
    
    // Pagination
    paginatedProducts() {
      const startIndex = (this.currentPage - 1) * this.itemsPerPage;
      const endIndex = startIndex + this.itemsPerPage;
      return this.sortedProducts.slice(startIndex, endIndex);
    },
    
    totalPages() {
      return Math.ceil(this.sortedProducts.length / this.itemsPerPage);
    },
    
    visiblePages() {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(this.totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    
    // Vérification si des filtres sont actifs
    isFiltered() {
      return this.searchQuery || 
             this.selectedCategories.length > 0 || 
             this.selectedBrands.length > 0 || 
             this.priceRange[0] > 0 || 
             this.priceRange[1] < this.maxPrice ||
             this.inStockOnly ||
             this.promoOnly ||
             this.minRating > 0;
    },
    
    // Nombre de filtres actifs
    activeFilterCount() {
      let count = 0;
      if (this.searchQuery) count++;
      if (this.selectedCategories.length) count++;
      if (this.selectedBrands.length) count++;
      if (this.priceRange[0] > 0 || this.priceRange[1] < this.maxPrice) count++;
      if (this.inStockOnly) count++;
      if (this.promoOnly) count++;
      if (this.minRating > 0) count++;
      return count;
    },
    
    // Vérification de l'authentification
    isAuthenticated() {
      return this.$store.getters.isAuthenticated;
    }
  },
  methods: {
    // Image handling - adapté à la nouvelle structure
    getProductImage(product) {
      if (product.cover_image) {
        return `${this.$store.state.baseUrl}${product.cover_image}`;
      }
      
      if (product.images && product.images.length > 0) {
        return `${this.$store.state.baseUrl}${product.images[0].image_path}`;
      }
      
      return this.defaultProductImage;
    },
    
    handleImageError(event) {
      event.target.src = this.defaultProductImage;
    },
    
    // Price handling
    formatPrice(price) {
      if (!price && price !== 0) return '0 FCFA';
      return new Intl.NumberFormat('fr-FR').format(price) + ' FCFA';
    },
    
    getProductPrice(product) {
      return parseFloat(product.promo_price) || parseFloat(product.price) || 0;
    },
    
    hasDiscount(product) {
      const price = parseFloat(product.price) || 0;
      const promoPrice = parseFloat(product.promo_price) || price;
      return promoPrice < price && price > 0;
    },
    
    calculateDiscount(product) {
      if (this.hasDiscount(product)) {
        const price = parseFloat(product.price) || 0;
        const promoPrice = parseFloat(product.promo_price) || price;
        const discount = ((price - promoPrice) / price) * 100;
        return Math.round(discount);
      }
      return 0;
    },
    
    // Badge handling
    getProductBadge(product) {
      if (this.hasDiscount(product)) {
        return 'Promotion';
      }
      
      if (product.created_at) {
        const isNew = new Date(product.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        if (isNew) {
          return 'Nouveau';
        }
      }
      
      if (product.view_count > 100) {
        return 'Tendance';
      }
      
      if (product.rating_avg >= 4.5) {
        return 'Best Seller';
      }
      
      return null;
    },
    
    getBadgeClass(badge) {
      const badgeClasses = {
        'Tendance': 'bg-orange-500 text-white px-2 py-1 rounded-sm text-xs font-medium',
        'Best Seller': 'bg-secondary text-secondary-foreground px-2 py-1 rounded-sm text-xs font-medium',
        'Nouveau': 'bg-green-500 text-white px-2 py-1 rounded-sm text-xs font-medium',
        'Promotion': 'bg-destructive text-destructive-foreground px-2 py-1 rounded-sm text-xs font-medium',
        'default': 'bg-primary text-primary-foreground px-2 py-1 rounded-sm text-xs font-medium'
      };
      
      return badgeClasses[badge] || badgeClasses.default;
    },
    
    // Compte des produits par catégorie
    getProductCountByCategory(categoryId) {
      return this.products.filter(p => {
        const productCategoryId = p.category?.id || p.model?.category?.id;
        return productCategoryId === categoryId;
      }).length;
    },
    
    // Filter methods
    handleCategoryToggle(categoryId) {
      const index = this.selectedCategories.indexOf(categoryId);
      if (index > -1) {
        this.selectedCategories.splice(index, 1);
      } else {
        this.selectedCategories.push(categoryId);
      }
      this.currentPage = 1;
    },
    
    handleBrandToggle(brandId) {
      const index = this.selectedBrands.indexOf(brandId);
      if (index > -1) {
        this.selectedBrands.splice(index, 1);
      } else {
        this.selectedBrands.push(brandId);
      }
      this.currentPage = 1;
    },
    
    removeCategory(categoryId) {
      this.selectedCategories = this.selectedCategories.filter(id => id !== categoryId);
      this.currentPage = 1;
    },
    
    removeBrand(brandId) {
      this.selectedBrands = this.selectedBrands.filter(id => id !== brandId);
      this.currentPage = 1;
    },
    
    resetPriceRange() {
      this.priceRange = [0, this.maxPrice];
      this.currentPage = 1;
    },
    
    getCategoryName(categoryId) {
      const category = this.availableCategories.find(c => c.id === categoryId);
      return category ? category.name : 'Catégorie';
    },
    
    getBrandName(brandId) {
      const brand = this.availableBrands.find(b => b.id === brandId);
      return brand ? brand.name : 'Marque';
    },
    
    updatePriceRange() {
      // Ensure min <= max
      if (this.priceRange[0] > this.priceRange[1]) {
        this.priceRange = [this.priceRange[1], this.priceRange[0]];
      }
      this.currentPage = 1;
    },
    
    clearFilters() {
      this.selectedCategories = [];
      this.selectedBrands = [];
      this.priceRange = [0, this.maxPrice];
      this.searchQuery = '';
      this.inStockOnly = false;
      this.promoOnly = false;
      this.minRating = 0;
      this.currentPage = 1;
      this.showFilters = false;
    },
    
    // Wishlist methods
    toggleWishlist(product) {
      if (!this.isAuthenticated) {
        this.$router.push('/auth');
        return;
      }
      
      if (this.isInWishlist(product.id)) {
        this.$store.dispatch('removeFromWishlist', product.id);
      } else {
        this.$store.dispatch('addToWishlist', product);
      }
    },
    
    isInWishlist(productId) {
      return this.wishlist.some(item => item.product_id === productId);
    },
    
    // Cart methods
    async addToCart(product) {
      if (!this.isAuthenticated) {
        this.$router.push('/auth');
        return;
      }
      
      if (!product.in_stock || product.stock_quantity <= 0) {
        alert('Ce produit n\'est pas disponible');
        return;
      }
      
      try {
        const cartData = {
          id: product.id,
          quantity: 1,
          size: product.sizes ? product.sizes[0] : null,
          color: product.colors ? product.colors[0] : null
        };
        
        await this.$store.dispatch('addToCart', cartData);
        
        this.showNotification('Produit ajouté au panier');
      } catch (error) {
        console.error('Error adding to cart:', error);
        if (error.response && error.response.data.error) {
          alert(error.response.data.error);
        } else {
          alert('Erreur lors de l\'ajout au panier');
        }
      }
    },
    
    showNotification(message) {
      // Utiliser une librairie de toast si disponible
      // alert(message);
    },
    
    // Pagination methods
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    
    goToPage(page) {
      this.currentPage = page;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    
    // Watch for route changes
    handleRouteChange() {
      this.clearFilters();
      this.currentPage = 1;
    }
  },
  watch: {
    '$route.params.category': {
      handler: 'handleRouteChange',
      immediate: true
    },
    
    searchQuery() {
      this.currentPage = 1;
    },
    
    sortBy() {
      this.currentPage = 1;
    },
    
    inStockOnly() {
      this.currentPage = 1;
    },
    
    promoOnly() {
      this.currentPage = 1;
    },
    
    minRating() {
      this.currentPage = 1;
    }
  },
  mounted() {
    // Mettre à jour la plage de prix initiale
    this.$store.dispatch('fetchProducts')
    this.$nextTick(() => {
      this.priceRange[1] = this.maxPrice;
    });
  }
};
</script>

<style scoped>
/* Custom styles for range input */
input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  background: hsl(var(--primary));
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid hsl(var(--background));
  box-shadow: var(--shadow-sm);
}

input[type="range"]::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: hsl(var(--primary));
  border-radius: 50%;
  cursor: pointer;
  border: 2px solid hsl(var(--background));
  box-shadow: var(--shadow-sm);
}

/* Line clamp for text */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Transition utilities */
.transition-normal {
  transition: all 0.2s ease-in-out;
}

/* Scrollbar styling for filter lists */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: hsl(var(--muted));
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: hsl(var(--muted-foreground) / 0.5);
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: hsl(var(--muted-foreground));
}

/* Gradient backgrounds */
.bg-gradient-hero {
  background: linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--secondary)) 100%);
}

/* Focus styles */
input:focus, select:focus, button:focus {
  outline: none;
  box-shadow: 0 0 0 2px hsl(var(--ring));
}

/* Loading spinner */
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
